// RequestsGrid.jsx
import React, { useState, useEffect } from 'react';
import { Paper, Typography, IconButton, Switch, FormControlLabel, Box } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import QuoteIcon from '@mui/icons-material/MonetizationOn';
import GiveQuoteModal from './giveQuoteModal';
import { toast } from 'react-toastify';

// Function to fetch open requests
const getOpenRequests = async (token, agencyID) => {
  const baseURL = process.env.REACT_APP_BASE_URL;
  try {
    const response = await fetch(`${baseURL}PostingTransferBond/${agencyID}/OpenRequests`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching open requests: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching open requests:', error);
    throw error;
  }
};

const RequestsGrid = ({ selectedAgency, refreshToggle, refreshHandler, canViewRequestingAgency }) => { // Accept selectedAgency as a prop
  // Removed activeTabRequests state
  const [showPreferred, setShowPreferred] = useState(false); // New state for switch
  const [eligibleRequests, setEligibleRequests] = useState([]); // All requests in the agent's counties
  const [preferredRequests, setPreferredRequests] = useState([]); // Preferred requests in the agent's counties
  const [loading, setLoading] = useState(true); // Loading state
  const [isGiveQuoteModalOpen, setGiveQuoteModalOpen] = useState(false); // State for Give Quote Modal
  const [selectedRowData, setSelectedRowData] = useState(null); // State to store the selected row data for the Give Quote Modal

  // Function to fetch requests data
  const fetchRequestsData = async () => {
    const token = localStorage.getItem('jwtToken');
    if (!token || !selectedAgency) {
      toast.error('No authentication token or selected agency found.');
      setLoading(false);
      return;
    }

    try {
      const data = await getOpenRequests(token, selectedAgency.id);

      if (data.allRequests && data.allRequests.length >= 0) {
        setEligibleRequests(data.allRequests.map((request) => {
          const dateObj = new Date(request.createdAt);
          const dateStr = dateObj.toLocaleDateString('en-US');
          const timeStr = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          return {
            ...request,
            id: request.transferBondTransactionID,
            defendantName: `${request.defendantFirstName?.trim() || ''} ${request.defendantLastName?.trim() || ''}`.trim() || 'N/A',
            createdAtFormatted: { date: dateStr, time: timeStr }
          };
        }));
      }

      if (data.preferredRequests && data.preferredRequests.length >= 0) {
        setPreferredRequests(data.preferredRequests.map((request) => {
          const dateObj = new Date(request.createdAt);
          const dateStr = dateObj.toLocaleDateString('en-US');
          const timeStr = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          return {
            ...request,
            id: request.transferBondTransactionID,
            defendantName: `${request.defendantFirstName?.trim() || ''} ${request.defendantLastName?.trim() || ''}`.trim() || 'N/A',
            createdAtFormatted: { date: dateStr, time: timeStr }
          };
        }));
      }
    } catch (error) {
      toast.error('Error fetching requests');
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount and when selectedAgency or refreshToggle changes
  useEffect(() => {
    fetchRequestsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgency, refreshToggle]); // Re-fetch data on refreshToggle change

  // Function to handle opening the Give Quote Modal
  const handleGiveQuote = (rowData) => {
    setSelectedRowData(rowData); // Store the selected row data
    setGiveQuoteModalOpen(true); // Open the Give Quote Modal
  };

  // Function to handle closing the Give Quote Modal
  const handleCloseGiveQuoteModal = () => {
    setGiveQuoteModalOpen(false); // Close the Give Quote Modal
  };

  // Define columns for the DataGrid
  const requestColumns = [
    {
      field: 'transferBondTransactionID',
      headerName: 'Ref #',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          height: '100%',
          width: '100%'
        }}>
          <Typography variant="body2">{params.value}</Typography>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleGiveQuote(params.row)}
          >
            <QuoteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    { field: 'county', headerName: 'County', width: 150 },
    { field: 'state', headerName: 'State', width: 100 },
    { field: 'jailLocation', headerName: 'Jail', width: 200, valueGetter: (params) => params || '-' },
    { field: 'courtPlace', headerName: 'Courthouse', width: 200, valueGetter: (params) => params || '-' },
    { field: 'numberOfBonds', headerName: 'Number of Bonds', width: 150 },
    { field: 'defendantName', headerName: 'Defendant Name', width: 250 },
    ...(canViewRequestingAgency
      ? [
          {
            field: 'requestingAgencyName',
            headerName: 'Requesting Agency',
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                  width: '100%',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                }}
              >
                {params.row.requestingAgencyName || '-'}
              </Box>
            ),
          },
        ]
      : []),
    {
      field: 'createdAtFormatted',
      headerName: 'Created At',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2">{params.value.date}</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
            {params.value.time}
          </Typography>
        </Box>
      ),
    },
  ];

  // Function to handle the switch toggle
  const handleSwitchToggle = (event) => {
    setShowPreferred(event.target.checked);
  };

  return (
    <Paper sx={{ padding: 3, mb: 4 }}>
      {/* Header Container */}
      <Box sx={{ mb: 2 }}>
        {/* Dynamic Header with Request Counts */}
        <Typography variant="h6" sx={{ mb: 1 }}>
          {showPreferred ? 'Requests in my Preferred Counties' : 'Requests in my Counties'} ({showPreferred ? preferredRequests.length : eligibleRequests.length})
        </Typography>

        {/* Switch placed below the header */}
        <FormControlLabel
          control={
            <Switch
              checked={showPreferred}
              onChange={handleSwitchToggle}
              color="primary"
            />
          }
          label={showPreferred ? 'Show All Requests' : 'Only Preferred Counties'}
        />
      </Box>

      {/* DataGrid displaying requests based on the switch state */}
      <Paper sx={{ width: '100%' }}>
        <DataGrid
          rows={showPreferred ? preferredRequests : eligibleRequests}
          columns={requestColumns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 }, // Set initial page size to 10
            },
          }}
          pageSizeOptions={[10]} // Limit rows per page options to only 10
          pagination // Enable pagination
          loading={loading}
          autoHeight
          disableSelectionOnClick
          sx={{ fontSize: '0.85rem' }} // Apply font size as per reference
        />
      </Paper>

      {/* Give Quote Modal */}
      <GiveQuoteModal
        open={isGiveQuoteModalOpen}
        onClose={handleCloseGiveQuoteModal}
        rowData={selectedRowData}
        selectedAgency={selectedAgency} // Pass selectedAgency to GiveQuoteModal
        refreshHandler={refreshHandler} // Pass refreshHandler to GiveQuoteModal
      />
    </Paper>
  );
};

export default RequestsGrid;
